import { useCallback, useEffect, useState } from 'react';

import { Box, Flex } from '@chakra-ui/react';
import { Add, Button, Chip } from '@mesh.ai/rabbit';
import { ColDef, RowClickedEvent } from 'ag-grid-community';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import { useToast } from 'components/hooks/useToast';
import { useHistory, useLocation } from 'react-router-dom';

import { AgGridHeader } from 'uiToolkitV1/AgGridHeader';
import { AgGridTable } from 'uiToolkitV1/AgGridWrapperV2';
import { AgGridKebabMenuOptions } from 'uiToolkitV1/AgGridWrapperV2/types';
import { TextHeader4, TextLabel } from 'uiToolkitV1/Typography';

import { AddQuestionBottomDrawer } from './AddQuestionBottomDrawer';
import { ANSWER_TYPE_OPTIONS } from './components/AnswerTypeDropdown';
import { OPEN_ENGAGEMENT_QUESTION_PANEL } from './constants/AddQuestionConstant';
import {
  ResponseEngagementQuestionType,
  useGetEngagementQuestionsList,
  useMutateAddEngagementQuestion,
} from './hooks/dataHooks';

export const QuestionLibrary = () => {
  const history = useHistory();
  const location = useLocation();
  const toast = useToast();

  const [selectedTab, setSelectedTab] = useState('ACTIVE');
  const { data: questions, isLoading } = useGetEngagementQuestionsList({
    label: selectedTab,
    source: 'ENGAGEMENT',
    limit: 2000,
    offset: 0,
  });

  const [questionsCount, setQuestionsCount] = useState<Record<'ACTIVE' | 'ARCHIVE', string>>({
    ACTIVE: '',
    ARCHIVE: '',
  });

  useEffect(() => {
    if (questions !== undefined) {
      setQuestionsCount((prev) => ({
        ...prev,
        [selectedTab]: questions?.length,
      }));
    }
  }, [questions, questions?.length, selectedTab]);

  const { mutate: archiveEngagementQuestion } = useMutateAddEngagementQuestion();

  const handleAddQuestion = () => {
    const queryParams = new URLSearchParams(location.search);
    queryParams.set('action', 'add');
    queryParams.set(OPEN_ENGAGEMENT_QUESTION_PANEL, String(true));
    history.push({
      search: queryParams.toString(),
    });
  };

  const columnDefs: ColDef<ResponseEngagementQuestionType>[] = [
    { field: 'description', headerName: 'Name', flex: 2 },
    {
      field: 'answerType',
      headerName: 'Type',
      flex: 1,
      filter: 'agSetColumnFilter',
      valueFormatter: ({ data }) => {
        const value = data.answerType;
        return ANSWER_TYPE_OPTIONS.find(({ value: v }) => v === value)?.label;
      },
      filterParams: {
        values: ANSWER_TYPE_OPTIONS.map(({ value }) => value),
        valueFormatter: ({ value }) => ANSWER_TYPE_OPTIONS.find(({ value: v }) => v === value)?.label,
      },
    },
    { field: 'tag.displayName', headerName: 'Driver', flex: 1 },
  ];

  const onRowClicked = useCallback(
    (event: RowClickedEvent<ResponseEngagementQuestionType, unknown>, action: 'add' | 'edit' | 'copy' = 'edit') => {
      const questionId = event?.data?.id;
      const queryParams = new URLSearchParams(location.search);
      queryParams.set('questionId', String(questionId));
      queryParams.set('action', action);
      queryParams.set('source', 'ENGAGEMENT');
      queryParams.set(OPEN_ENGAGEMENT_QUESTION_PANEL, String(true));
      history.push({
        search: queryParams.toString(),
      });
    },
    [history, location.search]
  );

  const onArchiveQuestion = useCallback(
    (question, isArchive: boolean) => {
      archiveEngagementQuestion({
        question: {
          ...question,
          isArchived: isArchive,
          isArchive,
        },
        isArchive,
      });
    },
    [archiveEngagementQuestion]
  );

  const kebabMenuOptionsGenerator = useCallback(
    (data: ResponseEngagementQuestionType) => {
      const options = [] as AgGridKebabMenuOptions;
      if (data?.isArchived === false) {
        options.push(
          {
            label: 'Edit',
            onClick: () => {
              const event = { data } as RowClickedEvent<ResponseEngagementQuestionType, unknown>;
              onRowClicked(event, 'edit');
            },
          },
          {
            label: 'Duplicate',
            onClick: () => {
              const event = { data } as RowClickedEvent<ResponseEngagementQuestionType, unknown>;
              onRowClicked(event, 'copy');
            },
          },
          {
            label: 'Archive',
            onClick: () => {
              const event = { data } as RowClickedEvent<ResponseEngagementQuestionType, unknown>;
              onArchiveQuestion(event.data, true);
              toast({
                title: 'Question archived successfully',
                status: 'success',
              });
            },
          }
        );
      } else {
        options.push(
          {
            label: 'Edit',
            onClick: () => {
              const event = { data } as RowClickedEvent<ResponseEngagementQuestionType, unknown>;
              onRowClicked(event, 'edit');
            },
          },
          {
            label: 'Unarchive',
            onClick: () => {
              const event = { data } as RowClickedEvent<ResponseEngagementQuestionType, unknown>;
              onArchiveQuestion(event.data, false);
              toast({
                title: 'Question unarchived successfully',
                status: 'success',
              });
            },
          }
        );
      }
      return options;
    },
    [onArchiveQuestion, onRowClicked, toast]
  );

  return (
    <Flex flexDir="column" p="12px" gap="12px">
      {/* Header */}
      <Flex justifyContent="space-between" p="4px 8px">
        <Box>
          <TextHeader4 bold>Question Library</TextHeader4>
          <TextLabel>Design and view sample or archived questions for your engagement surveys.</TextLabel>
        </Box>
        <Button
          iconLeft={Add}
          variant="outline"
          style={{ height: '32px', whiteSpace: 'nowrap' }}
          onClick={handleAddQuestion}
        >
          Add new
        </Button>
      </Flex>
      {/* Chips */}
      <Flex gap="8px">
        <Chip
          label={`Active ${questionsCount.ACTIVE !== '' ? `(${questionsCount.ACTIVE})` : ''}`}
          selected={selectedTab === 'ACTIVE'}
          onSelectedChange={() => {
            setSelectedTab('ACTIVE');
          }}
        />
        <Chip
          label={`Archive ${questionsCount.ARCHIVE !== '' ? `(${questionsCount.ARCHIVE})` : ''}`}
          selected={selectedTab === 'ARCHIVE'}
          onSelectedChange={() => {
            setSelectedTab('ARCHIVE');
          }}
        />
      </Flex>
      {/* Table */}
      <Box w="100%">
        <AgGridTable
          defaultColDef={{
            resizable: false,
            autoHeight: true,
            lockVisible: true,
            // wrapText: true,
            headerComponentParams: {
              enableMenu: true,
              enableSorting: true,
            },
            menuTabs: ['filterMenuTab'],
            headerComponent: AgGridHeader,
            filter: 'agTextColumnFilter',
            cellStyle: { display: 'flex', alignItems: 'center', overflow: 'hidden' },
          }}
          columnDefs={columnDefs}
          rowData={questions}
          domLayout="autoHeight"
          pagination
          suppressPaginationPanel={!questions?.length || questions.length < 10}
          paginationPageSize={10}
          headerHeight={40}
          containerAutoHeight
          showSidebarToggle={false}
          suppressCellFocus
          loading={isLoading}
          onRowClicked={onRowClicked}
          kebabMenuIsRowSpecific
          kebabMenuOptionsGenerator={kebabMenuOptionsGenerator}
        />
      </Box>
    </Flex>
  );
};
